/*
 * Timeseek market card image fallback.
 *
 * Replaces the inline onerror attribute that timeseek-brand-tabs used to emit
 * on every external market card image (about 570 bytes per card). One
 * capture-phase listener handles every market card image, including cards
 * that timeseek-brand-filters.js re-renders from captured markup.
 *
 * The placeholder comes from the image's own data-ts-placeholder-src. The
 * data-ts-fallback-applied flag is shared with global.js, so either handler
 * applies the fallback at most once and a failing placeholder cannot loop.
 */
(() => {
  if (window.TimeseekMarketImageFallback) return;

  const IMAGE_SELECTOR = '.ts-brand-tabs__market-media img[data-ts-placeholder-src]';
  const IMAGE_CLASSES_TO_REMOVE = [
    'ts-brand-tabs__market-image--external',
    'ts-brand-tabs__market-image--external-trimmed',
    'ts-brand-tabs__market-image--external-raw',
  ];
  const STAGE_SELECTOR = '.ts-brand-tabs__market-media-stage';
  const STAGE_CLASSES_TO_REMOVE = [
    'ts-brand-tabs__market-media-stage--external-trimmed',
    'ts-brand-tabs__market-media-stage--external-raw',
  ];

  const isMarketImage = (node) =>
    Boolean(node) && node.tagName === 'IMG' && typeof node.matches === 'function' && node.matches(IMAGE_SELECTOR);

  const applyFallback = (image) => {
    if (!isMarketImage(image)) return false;
    if (image.getAttribute('data-ts-fallback-applied') === 'true') return false;

    const placeholder = image.getAttribute('data-ts-placeholder-src') || '';
    if (!placeholder) return false;

    image.setAttribute('data-ts-fallback-applied', 'true');
    image.removeAttribute('srcset');
    image.setAttribute('src', placeholder);
    image.classList.remove(...IMAGE_CLASSES_TO_REMOVE);
    image.classList.add('ts-brand-tabs__market-image--placeholder');

    const stage = typeof image.closest === 'function' ? image.closest(STAGE_SELECTOR) : null;
    if (stage) stage.classList.remove(...STAGE_CLASSES_TO_REMOVE);
    return true;
  };

  const isBroken = (image) => Boolean(image.getAttribute('src')) && image.complete === true && image.naturalWidth === 0;

  // Images that failed before this deferred script ran never fire another
  // error event, so sweep the ones that already finished broken.
  const sweep = (root) => {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    let applied = 0;
    scope.querySelectorAll(IMAGE_SELECTOR).forEach((image) => {
      if (isBroken(image) && applyFallback(image)) applied += 1;
    });
    return applied;
  };

  // error does not bubble, so listen in the capture phase.
  document.addEventListener('error', (event) => applyFallback(event.target), true);
  document.addEventListener('shopify:section:load', (event) => sweep(event.target));

  window.TimeseekMarketImageFallback = { applyFallback, sweep };
  sweep(document);
})();
